import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { getEntityDetailRoute } from "@/src/utils/entityRouteUtils.js";

export function analyzeRelationship(
  relationship,
  currentEntitySlug,
  targetObjectId
) {
  const sourceObjectSlug = String(
    relationship?.source_object?.slug || ""
  ).toLowerCase();

  const isSourceTickets = sourceObjectSlug === entityTypes.TICKETS;
  const isTargetTickets =
    String(targetObjectId).toLowerCase() === entityTypes.TICKETS;
  const isSourceTasks = sourceObjectSlug === entityTypes.TASKS;
  const isTargetTasks =
    String(targetObjectId).toLowerCase() === entityTypes.TASKS;

  const isCurrentTickets = currentEntitySlug === entityTypes.TICKETS;
  const isCurrentTasks = currentEntitySlug === entityTypes.TASKS;

  const ticketsInvolved =
    !isCurrentTickets && (isSourceTickets || isTargetTickets);
  const tasksInvolved = !isCurrentTasks && (isSourceTasks || isTargetTasks);

  return {
    sourceObjectSlug,
    isSourceTickets,
    isTargetTickets,
    isSourceTasks,
    isTargetTasks,
    isCurrentTickets,
    isCurrentTasks,
    ticketsInvolved,
    tasksInvolved,
  };
}

export function getRelationshipServiceType(analysis) {
  if (analysis.isCurrentTickets || analysis.ticketsInvolved) {
    return entityTypes.TICKETS;
  }
  if (analysis.isCurrentTasks || analysis.tasksInvolved) {
    return entityTypes.TASKS;
  }
  return "crm";
}

export function getRelatedObject(relationship, currentEntitySlug) {
  if (!relationship) return null;

  const currentSlug = String(currentEntitySlug || "").toLowerCase();

  let targetSlug = relationship.target_object?.slug || relationship.target_object_id;
  let sourceSlug = relationship.source_object?.slug || relationship.source_object_id;

  if (targetSlug) targetSlug = String(targetSlug).toLowerCase();
  if (sourceSlug) sourceSlug = String(sourceSlug).toLowerCase();

  if (targetSlug === currentSlug && sourceSlug) {
    return {
      object: relationship.source_object,
      slug: sourceSlug,
      objectId: relationship.source_object_id,
    };
  }

  if (sourceSlug === currentSlug && targetSlug) {
    return {
      object: relationship.target_object,
      slug: targetSlug,
      objectId: relationship.target_object_id,
    };
  }

  return {
    object: relationship.target_object || relationship.source_object,
    slug: targetSlug || sourceSlug,
    objectId: relationship.target_object_id || relationship.source_object_id,
  };
}

export function getRelatedObjectSlug(relationship, currentEntitySlug) {
  const relatedInfo = getRelatedObject(relationship, currentEntitySlug);
  return relatedInfo?.slug || "";
}

export async function fetchRelationshipConnections({
  serviceType,
  services,
  sourceEntityId,
  targetObjectId,
  currentObjectType,
  projectId,
  page = 1,
  pageSize = 10,
  service = null,
  targetService = null,
  relationshipId = null,
}) {
  const { TicketsService, ProjectService, CRMService } = services;

  switch (serviceType) {
    case entityTypes.TICKETS:
      return await TicketsService.getRelatedObjects(
        sourceEntityId,
        currentObjectType,
        targetObjectId,
        { page, page_size: pageSize }
      );

    case entityTypes.TASKS:
      if (!projectId) {
        const crmParams = {
          object_id: sourceEntityId,
          object_type: currentObjectType,
          related_object_type: targetObjectId,
          page,
          page_size: pageSize,
          // `service` = owning service of the SOURCE object; the backend reads
          // this to resolve same-slug objects across services (e.g. the
          // products-service products vs. a CRM-scoped custom products).
          ...(service ? { service } : {}),
          ...(targetService ? { target_service: targetService } : {}),
          ...(relationshipId ? { relationship_id: relationshipId } : {}),
        };
        return await CRMService.getRelationshipConnections(crmParams);
      }
      return await ProjectService.tasks.listTaskRelationships(
        projectId,
        sourceEntityId
      );

    case "crm":
    default:
      const params = {
        object_id: sourceEntityId,
        object_type: currentObjectType,
        related_object_type: targetObjectId,
        page,
        page_size: pageSize,
        ...(service ? { service } : {}),
        ...(targetService ? { target_service: targetService } : {}),
        ...(relationshipId ? { relationship_id: relationshipId } : {}),
      };
      return await CRMService.getRelationshipConnections(params);
  }
}

export async function createRelationshipConnection({
  serviceType,
  services,
  sourceEntityId,
  newEntityId,
  targetObjectType,
  relationshipId,
  currentObjectType,
  projectId,
  analysis,
  service = null,
}) {
  const { TicketsService, ProjectService, CRMService } = services;

  switch (serviceType) {
    case entityTypes.TICKETS: {
      const isCurrentTickets = analysis.isCurrentTickets;
      const ticketId = isCurrentTickets ? sourceEntityId : String(newEntityId);
      const relatedObjectId = isCurrentTickets
        ? String(newEntityId)
        : sourceEntityId;
      const relatedObjectType = isCurrentTickets
        ? targetObjectType
        : currentObjectType;

      const ticketPayload = {
        target_object_type_id: relatedObjectType,
        related_object_id: relatedObjectId,
        relationship_id: relationshipId,
      };

      return await TicketsService.createTicketRelationship(
        ticketId,
        ticketPayload
      );
    }

    case entityTypes.TASKS: {
      if (!projectId || !analysis.isCurrentTasks) {
        const crmPayload = {
          object_id: sourceEntityId,
          object_type: currentObjectType,
          related_object_type: targetObjectType,
          target_object_type_id: targetObjectType,
          related_object_id: String(newEntityId),
          relationship_id: relationshipId,
        };
        return await CRMService.createRelationshipConnection(crmPayload, service);
      }

      const taskPayload = {
        target_task_id: String(newEntityId),
        relationship_type: relationshipId,
      };

      return await ProjectService.tasks.createTaskRelationship(
        projectId,
        sourceEntityId,
        taskPayload
      );
    }

    case "crm":
    default: {
      const payload = {
        object_id: sourceEntityId,
        object_type: currentObjectType,
        related_object_type: targetObjectType,
        target_object_type_id: targetObjectType,
        related_object_id: String(newEntityId),
        relationship_id: relationshipId,
      };

      return await CRMService.createRelationshipConnection(payload, service);
    }
  }
}

export async function deleteRelationshipConnection({
  serviceType,
  services,
  relationshipId,
  sourceEntityId,
  projectId,
  service = null,
}) {
  const { TicketsService, ProjectService, CRMService } = services;

  switch (serviceType) {
    case entityTypes.TICKETS:
      return await TicketsService.deleteTicketRelationship(relationshipId);

    case entityTypes.TASKS:
      if (!projectId) {
        return await CRMService.deleteRelationshipConnection(
          relationshipId,
          service,
        );
      }
      return await ProjectService.tasks.deleteTaskRelationship(
        projectId,
        sourceEntityId,
        relationshipId
      );

    case "crm":
    default:
      return await CRMService.deleteRelationshipConnection(
        relationshipId,
        service,
      );
  }
}

export async function checkExistingAssociations({
  serviceType,
  services,
  targetIds,
  sourceEntityId,
  relationshipId,
  targetObjectType,
  currentObjectType,
}) {
  const { TicketsService, CRMService } = services;
  const stringIds = (targetIds || []).map((id) => String(id));

  const payload = {
    relationship_id: relationshipId,
    resource_object_type_id: targetObjectType,
    resource_object_ids: stringIds,
  };

  if (serviceType === entityTypes.TICKETS) {
    return await TicketsService.getObjectsWithRelationships(payload);
  }

  if (serviceType === entityTypes.TASKS) {
    console.warn(
      "Task relationship conflict checking not yet implemented - using CRM service as fallback"
    );
  }

  return await CRMService.getObjectsWithRelationships(payload);
}

export async function bulkCreateRelationshipConnections({
  serviceType,
  services,
  sourceEntityId,
  targetIds,
  targetObjectType,
  relationshipId,
  currentObjectType,
  projectId,
  analysis,
  service = null,
}) {
  const { TicketsService, ProjectService, CRMService } = services;
  const stringIds = (targetIds || []).map((id) => String(id));

  switch (serviceType) {
    case entityTypes.TICKETS: {
      const isCurrentTickets = analysis.isCurrentTickets;

      if (isCurrentTickets) {
        const ticketPayload = {
          target_object_type_id: targetObjectType,
          target_object_ids: stringIds,
          relationship_id: relationshipId,
        };
        return await TicketsService.createTicketRelationships(
          sourceEntityId,
          ticketPayload
        );
      } else {
        const promises = stringIds.map((ticketId) => {
          const ticketPayload = {
            target_object_type_id: currentObjectType,
            related_object_id: sourceEntityId,
            relationship_id: relationshipId,
          };
          return TicketsService.createTicketRelationship(
            ticketId,
            ticketPayload
          );
        });

        const results = await Promise.allSettled(promises);
        const failedCount = results.filter(
          (r) => r.status === "rejected" || !r.value?.success
        ).length;

        if (failedCount === 0) {
          return { success: true };
        } else if (failedCount < results.length) {
          return {
            success: true,
            message: `Added ${results.length - failedCount} associations, ${failedCount} failed`,
          };
        } else {
          return { success: false, message: "Failed to create associations" };
        }
      }
    }

    case entityTypes.TASKS: {
      if (!projectId || !analysis.isCurrentTasks) {
        const crmPayload = {
          object_id: sourceEntityId,
          object_type: currentObjectType,
          target_object_type_id: targetObjectType,
          target_object_ids: stringIds,
          relationship_id: relationshipId,
        };
        return await CRMService.bulkCreateRelationships(crmPayload, service);
      }

      const relationships = stringIds.map((targetId) => ({
        source_task_id: sourceEntityId,
        target_task_id: targetId,
        relationship_type: relationshipId,
      }));

      const taskPayload = {
        relationships: relationships,
      };

      return await ProjectService.tasks.bulkCreateTaskRelationships(
        projectId,
        taskPayload
      );
    }

    case "crm":
    default: {
      const payload = {
        object_id: sourceEntityId,
        object_type: currentObjectType,
        target_object_type_id: targetObjectType,
        target_object_ids: stringIds,
        relationship_id: relationshipId,
      };

      return await CRMService.bulkCreateRelationships(payload, service);
    }
  }
}

export function navigateToEntityDetail({
  router,
  slug,
  id,
  projectId,
  spaceId,
  service = null,
}) {
  if (!slug || !id) {
    console.warn("Cannot navigate: missing slug or id");
    return;
  }

  const lowerSlug = String(slug).toLowerCase();

  // Delegate shared-slug objects (products / invoices / line_items) to the
  // service-aware router helper so the owning service picks its own pretty
  // route (e.g. /products/:id for service=products, /crm/products/:id for
  // service=crm) and the ?service= query is preserved.
  if (
    SERVICE_SCOPED_PATHS_DELEGATION.includes(lowerSlug) ||
    (service && service !== "crm")
  ) {
    const route = getEntityDetailRoute({
      entityType: lowerSlug,
      recordId: id,
      projectId,
      spaceId,
      service,
    });
    if (route && route !== "#") {
      router.push(route);
      return;
    }
  }

  const routeMap = {
    contacts: `/contacts/${id}`,
    companies: `/companies/${id}`,
    deals: `/deals/${id}`,
    leads: `/leads/${id}`,
    [entityTypes.TICKETS]: `/tickets/${id}`,
  };

  if (routeMap[lowerSlug]) {
    router.push(routeMap[lowerSlug]);
  } else if (lowerSlug === entityTypes.TASKS) {
    if (projectId) {
      const route = {
        path: `/spaces/projects/tasks/${id}`,
        query: {
          project_id: projectId,
        },
      };

      if (spaceId) {
        route.query.space_id = spaceId;
      }

      router.push(route);
    } else {
      console.warn("Cannot navigate to task without projectId");
    }
  } else {
    const route = getEntityDetailRoute({
      entityType: lowerSlug,
      recordId: id,
      projectId,
      spaceId,
      service,
    });
    router.push(route && route !== "#" ? route : `/crm/${lowerSlug}/${id}`);
  }
}

// Slugs with service-scoped pretty routes that must route via
// getEntityDetailRoute so the right service's URL is picked.
const SERVICE_SCOPED_PATHS_DELEGATION = ["products", "invoices", "line_items"];

export function getRelationshipSlugs(relationship) {
  if (!relationship) {
    return { sourceSlug: '', targetSlug: '' };
  }

  let targetSlug = '';
  if (relationship.target_object?.id && relationship.target_object?.slug) {
    targetSlug = relationship.target_object.slug.toLowerCase();
  } else {
    targetSlug = String(
      relationship.target_object_id || 
      relationship.target_object_type || 
      ''
    ).toLowerCase();
  }

  let sourceSlug = '';
  if (relationship.source_object?.id && relationship.source_object?.slug) {
    sourceSlug = relationship.source_object.slug.toLowerCase();
  } else {
    sourceSlug = String(
      relationship.source_object_id || 
      relationship.source_object_type || 
      ''
    ).toLowerCase();
  }

  return { sourceSlug, targetSlug };
}
